import { styled } from "@mui/material/styles";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import {
  buildNotesLabelDiff,
  buildTotalsNotesLabelDiff,
} from "../buildPatchDiff";
import { financialsRowKinds } from "../types";

import type { FinancialsGridContext, FinancialsRow } from "../types";
import type { ICellRendererParams } from "ag-grid-community";

const NotesLabelTextarea = styled("textarea")({
  width: "100%",
  border: "none",
  outline: "none",
  resize: "none",
  overflow: "hidden",
  boxSizing: "border-box",
  display: "block",
  backgroundColor: "transparent",
  fontFamily: "inherit",
  fontSize: "inherit",
  lineHeight: 1.4,
  textAlign: "left",
  // Browsers apply a default textarea padding (e.g. Chrome's UA stylesheet),
  // which the label column's other rows don't have — left unset, the "Notes"
  // text sits a couple pixels off from sibling row labels above it.
  padding: 0,
});

// A Notes row header label belongs to a coverage (the coverage NOTES row)
// or a totals block (the TOTAL_NOTES row) — never both. Resolving it up
// front lets the rest of the component read one value instead of branching
// on row kind at every use site.
type NotesLabelTarget =
  | { kind: "coverage"; coverageId: string }
  | { kind: "block"; blockId: string }
  | null;

const resolveTarget = (data?: FinancialsRow): NotesLabelTarget => {
  if (!data) return null;
  if (data.kind === financialsRowKinds.TOTAL_NOTES) {
    return { kind: "block", blockId: data.blockId ?? "default" };
  }
  return data.coverageId
    ? { kind: "coverage", coverageId: data.coverageId }
    : null;
};

export const NotesLabelCellRenderer = ({
  data,
  context,
}: ICellRendererParams<FinancialsRow>): React.JSX.Element | null => {
  const ctx = context as FinancialsGridContext;
  const target = resolveTarget(data);
  const serverValue =
    (target?.kind === "block"
      ? ctx.blockById.get(target.blockId)?.notesRowHeaderLabel?.trim()
      : target
        ? ctx.coverageById.get(target.coverageId)?.notesRowHeaderLabel?.trim()
        : undefined) ?? "";

  const [localValue, setLocalValue] = useState(serverValue);
  const isFocusedRef = useRef(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const resize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  useEffect(() => {
    if (!isFocusedRef.current) {
      setLocalValue(serverValue);
    }
  }, [serverValue]);

  useLayoutEffect(() => {
    resize();
  }, [localValue, resize]);

  const handleFocus = useCallback(() => {
    isFocusedRef.current = true;
  }, []);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      setLocalValue(event.target.value);
    },
    [],
  );

  const targetKind = target?.kind;
  const targetId =
    target?.kind === "block"
      ? target.blockId
      : target?.kind === "coverage"
        ? target.coverageId
        : undefined;

  const handleBlur = useCallback(() => {
    isFocusedRef.current = false;
    if (!targetKind || !targetId || localValue === serverValue) return;
    ctx.queueChange(
      targetKind === "block"
        ? buildTotalsNotesLabelDiff(
            targetId,
            localValue,
            ctx.payload.totalsBlocks,
          )
        : buildNotesLabelDiff(targetId, localValue, ctx.payload.coverages),
    );
  }, [targetKind, targetId, localValue, serverValue, ctx]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key !== "Enter") return;
      event.preventDefault();
      event.currentTarget.blur();
    },
    [],
  );

  if (!target) return null;

  return (
    <NotesLabelTextarea
      ref={textareaRef}
      value={localValue}
      rows={1}
      placeholder="Notes"
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      autoComplete="off"
      maxLength={140}
      aria-label="Notes row header"
    />
  );
};
